/* v3.18-1290 — Frontend mirror of ``backend/services/funding_sources_read.py``.
 *
 * Canonical funding-sources readers for the React app.  All UI code
 * that needs deal amount / paid-to-ptc / paid-to-partner / wire fee
 * MUST call one of these helpers.  Reading legacy flat fields
 * (``client.deal_amount``, ``client.amount_paid_to_ptc``, etc.)
 * directly is banned per PAYMENT-008B Slice 5.
 *
 * The helpers each accept a client object and return either the value
 * summed from ``funding_sources[]`` when present, or fall back to the
 * legacy flat field for un-migrated / synthesized records.  Callers
 * should treat these as the SINGLE source of truth.
 */

const num = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function _sources(client) {
  const arr = client && client.funding_sources;
  return Array.isArray(arr) ? arr : [];
}

/** Deal amount — sum of ``deal_amount`` (a.k.a. ``cc_amount``/
 *  ``ach_approved_amount``) across ``funding_sources[]``.  Falls
 *  back to legacy ``client.deal_amount`` for un-migrated deals. */
export function clientDealAmount(client) {
  const srcs = _sources(client);
  if (srcs.length) {
    return srcs.reduce((acc, s) => acc + num(s.deal_amount ?? s.cc_amount ?? s.ach_approved_amount), 0);
  }
  return num(client?.deal_amount);
}

/** Total wire fee — sum of ``wire_info.wire_transfer_fee`` across
 *  ``funding_sources[]``.  Falls back to legacy top-level
 *  ``client.wire_transfer_fee``. */
export function wireFee(client) {
  const srcs = _sources(client);
  if (srcs.length) {
    let sum = 0;
    let seen = false;
    for (const s of srcs) {
      const wi = s.wire_info || {};
      if (wi.wire_transfer_fee !== undefined && wi.wire_transfer_fee !== null && wi.wire_transfer_fee !== '') {
        sum += num(wi.wire_transfer_fee);
        seen = true;
      }
    }
    if (seen) return sum;
  }
  return num(client?.wire_transfer_fee);
}

/** Total paid to PTC — sum of ``wire_info.wire_paid_to_ptc``. */
export function paidToPtc(client) {
  const srcs = _sources(client);
  if (srcs.length) {
    let sum = 0;
    let seen = false;
    for (const s of srcs) {
      const wi = s.wire_info || {};
      if (wi.wire_paid_to_ptc !== undefined && wi.wire_paid_to_ptc !== null && wi.wire_paid_to_ptc !== '') {
        sum += num(wi.wire_paid_to_ptc);
        seen = true;
      }
    }
    if (seen) return sum;
  }
  return num(client?.amount_paid_to_ptc);
}

/** Total paid to Partner — sum of ``wire_info.wire_paid_to_partner``. */
export function paidToPartner(client) {
  const srcs = _sources(client);
  if (srcs.length) {
    let sum = 0;
    let seen = false;
    for (const s of srcs) {
      const wi = s.wire_info || {};
      if (wi.wire_paid_to_partner !== undefined && wi.wire_paid_to_partner !== null && wi.wire_paid_to_partner !== '') {
        sum += num(wi.wire_paid_to_partner);
        seen = true;
      }
    }
    if (seen) return sum;
  }
  return num(client?.amount_paid_to_partner ?? client?.paid_to_partner);
}
